import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Heading, Modal, Text } from '@zillow/constellation-10';

import {
  useRentalManagerGuestSupportWebApiHandler_getHelpArticles,
  useRentalManagerGuestSupportWebApiHandler_getHelpTopics,
  useRentalManagerUserSupportWebApiHandler_getHelpArticles,
  useRentalManagerUserSupportWebApiHandler_getHelpTopics,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { keepPreviousData } from '@zg-rentals/react-query5';
import { css } from '@/styled-system/css';

import { HelpArticlesContent } from './HelpArticles';
import { SpinnerContainer } from '../SpinnerContainer';
import { SubmitRequestForm } from './SubmitRequestForm';

import { LoadingConstants, useRentalManagerUser } from '../..';
import { emitOpenSupportWidgetEvent, emitSubmitWidgetSupportRequestEvent } from './ClickstreamEvents';

import type {
  GetHelpTopicListResponseV2Dto,
  GuestSupportGetHelpTopicsCategoryTopicWebDto,
  GuestSupportGetHelpTopicsCategoryWebDto,
  GuestSupportGetHelpTopicsResponseWebDto,
  HelpCategoryDto,
  HelpTopicV2Dto,
  RentalManagerAuthCheckUserInfoWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

export const footerStyles = css.raw({
  display: 'flex',
  justifyContent: { base: 'flex-end', lg: 'flex-start' },
  marginBlock: '600',
  lgDown: {
    borderBlock: '1px solid {colors.border.subtle}',
    position: 'fixed',
    width: '100%',
    bottom: 0,
    marginLeft: '{spacing.-400}',
    marginBottom: 0,
    padding: '400',
    background: 'bg.neutral',
    zIndex: `10000`,
  },
});

/** Help topics response from either user or guest support API (same shape, different DTOs). */
export type HelpTopicsResponse = GetHelpTopicListResponseV2Dto | GuestSupportGetHelpTopicsResponseWebDto;

export type FormStateType = {
  category?: HelpCategoryDto | GuestSupportGetHelpTopicsCategoryWebDto;
  topic?: HelpTopicV2Dto | GuestSupportGetHelpTopicsCategoryTopicWebDto;
  typedAddress: string;
  unit: string;
  description: string;
};

interface SubmitRequestModalPageProps {
  setCanSubmit: (val: boolean) => void;
  submitClicked: boolean;
  setSubmitClicked: (val: boolean) => void;
  setCategorySelected?: (val: boolean) => void;
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
  queryObject?: {
    categoryKey: string | null;
    topicKey: string | null;
    listingAddress: string | null;
    unit: string | null;
    listingAlias: string | null;
  };
  zrmSignInPage?: string;
  renderFooter?: () => JSX.Element;
}

/* Re-usable component that shows the submit request form and help articles side-by-side */
export const SubmitRequestPageComponent = ({
  setCanSubmit,
  submitClicked,
  setSubmitClicked,
  setCategorySelected,
  zrmwUser,
  queryObject,
  zrmSignInPage,
  renderFooter,
}: SubmitRequestModalPageProps) => {
  const [errorAlert, setErrorAlert] = useState('');
  const [requestState, setRequestState] = useState<LoadingConstants>(LoadingConstants.INITIAL);

  const [formState, setFormState] = useState<FormStateType>({
    category: undefined,
    topic: undefined,
    typedAddress: queryObject?.listingAddress || '',
    unit: queryObject?.unit || '',
    description: '',
  });

  const nonZrmwUser = useRentalManagerUser().user;
  const user = zrmwUser || nonZrmwUser;
  const { loggedIn } = user;
  const isUsingWidget = !renderFooter;

  const helpArticlesParams = { category: formState?.category?.key, topic: formState?.topic?.key };

  const { data: userHelpTopics, isError: userHelpTopicsError } = useRentalManagerUserSupportWebApiHandler_getHelpTopics(
    { enabled: loggedIn },
  );
  const { data: guestHelpTopics, isError: guestHelpTopicsError } =
    useRentalManagerGuestSupportWebApiHandler_getHelpTopics({ enabled: !loggedIn });
  const { data: userHelpArticles, isError: userHelpArticlesError } =
    useRentalManagerUserSupportWebApiHandler_getHelpArticles(helpArticlesParams, {
      enabled: loggedIn,
      placeholderData: keepPreviousData,
    });
  const { data: guestHelpArticles, isError: guestHelpArticlesError } =
    useRentalManagerGuestSupportWebApiHandler_getHelpArticles(helpArticlesParams, {
      enabled: !loggedIn,
      placeholderData: keepPreviousData,
    });

  const helpTopics = loggedIn ? userHelpTopics : guestHelpTopics;
  const helpArticles = loggedIn ? userHelpArticles : guestHelpArticles;

  const helpTopicsNotAvailable = helpTopics === undefined;
  const helpArticlesNotAvailable = helpArticles === undefined;

  useEffect(() => {
    emitOpenSupportWidgetEvent({ user });
  }, []);

  useEffect(() => {
    const errorMessage = 'An error has occurred. Please try again later.';
    if (loggedIn && userHelpTopicsError) setErrorAlert(errorMessage);
    if (!loggedIn && guestHelpTopicsError) setErrorAlert(errorMessage);
  }, [loggedIn, userHelpTopicsError, guestHelpTopicsError]);

  useEffect(() => {
    if (loggedIn && userHelpArticlesError) setErrorAlert('');
    if (!loggedIn && guestHelpArticlesError) setErrorAlert('');
  }, [loggedIn, userHelpArticlesError, guestHelpArticlesError]);

  useEffect(() => {
    if (setCategorySelected) setCategorySelected(Boolean(formState.category));
  }, [formState.category, setCategorySelected]);

  const showSpinner = useMemo(() => {
    return helpTopicsNotAvailable || helpArticlesNotAvailable || requestState === LoadingConstants.LOADING;
  }, [helpTopicsNotAvailable, helpArticlesNotAvailable, requestState]);

  const onSubmit = () => {
    if (isUsingWidget) {
      emitSubmitWidgetSupportRequestEvent({ user: zrmwUser || user });
    }
  };

  const renderSuccess = () => (
    <Alert tone="success" css={{ margin: '600' }}>
      <Text>Thank you! Your request has been submitted.</Text>
    </Alert>
  );

  const renderForm = () => {
    const formWidth = helpArticlesNotAvailable ? '100%' : '60%';
    return (
      <Box css={{ display: 'flex', justifyContent: 'space-between', xlDown: { flexDirection: 'column' } }}>
        <Box css={{ marginRight: '200' }} style={{ minWidth: formWidth }}>
          {!helpTopicsNotAvailable && !helpArticlesNotAvailable && (
            <SubmitRequestForm
              setCanSubmit={setCanSubmit}
              setFormState={setFormState}
              setRequestState={setRequestState}
              setErrorAlert={setErrorAlert}
              onSubmit={onSubmit}
              submitClicked={submitClicked}
              formState={formState}
              helpTopics={helpTopics}
              zrmwUser={zrmwUser}
              queryObject={queryObject}
              zrmSignInPage={zrmSignInPage}
            />
          )}
          {zrmwUser && renderFooter?.()}
        </Box>
        {!helpArticlesNotAvailable && <HelpArticlesContent data={helpArticles} error={false} />}
      </Box>
    );
  };

  const renderState = () => {
    switch (true) {
      case Boolean(requestState === LoadingConstants.ERROR):
        return (
          <Modal
            open={true}
            size={{ base: 'fullScreen', lg: 'md' }}
            header={<Heading level={1}>Unable to submit request</Heading>}
            body={<Text>{errorAlert}</Text>}
            onOpenChange={(open) => {
              if (!open) {
                setErrorAlert('');
                setSubmitClicked(false);
                setRequestState(LoadingConstants.INITIAL);
              }
            }}
          />
        );
      case Boolean(errorAlert):
        return (
          <Alert tone="critical">
            <Text>{errorAlert}</Text>
          </Alert>
        );
      case Boolean(requestState === LoadingConstants.SUCCESS):
        return renderSuccess();
      case Boolean(showSpinner):
        return <SpinnerContainer size="lg" height="600px" />;
      case Boolean(helpTopics?.categories?.length):
        return renderForm();
      default:
        return null;
    }
  };
  return <Box>{renderState()}</Box>;
};

export interface SubmitRequestPageProps {
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
  zrmSignInPage?: string;
}
